// src/features/messages/Composer.tsx — message input + attachment + emoji + Send.
//
// 1.24.00: the "+" is a REAL image picker now (one image per message, uploaded
// through the same mediaService seam the home composer uses, with purpose 'dm')
// and the emoji button opens the same EmojiPicker the home composer opens. The
// mic stays a DisabledHint because voice messages genuinely do not exist —
// that is the honest state, not a dead control.
import { useMemo, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { DisabledHint, IconButton, Button } from '@/ui';
import { EmojiPicker } from '@/features/home/composer/EmojiPicker';
import { mediaService, uploadErrorMessage } from '@/core/services/mediaService';
import { ApiError } from '@/core/lib/apiClient';
import type { OutgoingMessageImage } from '@/core/services/messagesService';

/**
 * Turn a failed send into copy a person can act on. The server answers
 * `400 {code:'validation', fields:{body|imageKey}}` — an empty box with no
 * photo, or an unusable image key. The composer already refuses to fire an
 * empty send, so `body` here means the two sides disagreed; say so plainly
 * rather than swallowing it.
 */
function sendErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.code === 'validation') {
    const fields = (e.fields ?? {}) as Record<string, unknown>;
    if ('imageKey' in fields) return "That photo couldn't be attached. Pick it again.";
    if ('body' in fields) return 'Type a message (or attach a photo) first.';
  }
  return "Couldn't send that message. Please try again.";
}

const Wrap = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Bar = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  resize: none;
  max-height: 120px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.glassBg};
  padding: 9px 12px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  outline: none;
  &:focus-visible {
    border-color: ${({ theme }) => theme.color.accentCyan};
  }
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const PreviewStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 0;
`;

const PreviewThumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const RemoveBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; }
`;

const ErrorLine = styled.p`
  margin: 0;
  padding: 6px 12px 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;

export interface ComposerProps {
  /**
   * `image` is already uploaded — the caller only forwards the key/dimensions.
   * May return a promise; when it does, a rejection surfaces inline here and
   * the draft comes back rather than vanishing into a silent failure.
   */
  onSend: (text: string, image?: OutgoingMessageImage) => void | Promise<void>;
  /** Fired on every keystroke; the realtime `typing` send is throttled inside sendTyping itself. */
  onTyping?: () => void;
  /**
   * When set, every control is inert and this string is the accessible
   * explanation (DisabledHint contract). Used for an unaccepted message
   * request — you cannot reply until you accept it.
   */
  disabledReason?: string;
}

export function Composer({ onSend, onTyping, disabledReason }: ComposerProps) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // One object URL per selected file, revoked when the selection changes so a
  // long chat session can't leak blobs.
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const disabled = disabledReason !== undefined;
  const trimmed = text.trim();
  // An image on its own IS a message — the server accepts an empty body when
  // imageKey is present, so the client must not insist on text.
  const canSend = !disabled && !busy && (trimmed !== '' || file !== null);

  function clearFileInput() {
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /**
   * Clear optimistically (the box has always emptied the instant you hit
   * Enter), then hand the draft BACK if the send rejected — but only if the
   * box is still empty, so a message typed during the round-trip is never
   * overwritten by the one that failed.
   */
  function restoreDraftOnFailure(draft: string, e: unknown) {
    setErr(sendErrorMessage(e));
    setText((cur) => (cur === '' ? draft : cur));
  }

  async function submit() {
    if (!canSend) return;
    setErr(null);
    if (!file) {
      setText('');
      try {
        await onSend(trimmed);
      } catch (e) {
        restoreDraftOnFailure(trimmed, e);
      }
      return;
    }
    setBusy(true);
    try {
      const up = await mediaService.upload('dm', file);
      try {
        await onSend(trimmed, {
          key: up.key,
          ...(up.width ? { width: up.width } : {}),
          ...(up.height ? { height: up.height } : {}),
        });
      } catch (e) {
        // Upload succeeded, send didn't: keep the photo AND the caption so the
        // whole message can be retried with one press.
        setErr(sendErrorMessage(e));
        return;
      }
      setText('');
      setFile(null);
      clearFileInput();
    } catch (e) {
      // The draft and the attachment both survive a failed upload — losing a
      // typed message because a photo failed would be its own bug.
      setErr(uploadErrorMessage(e, 'Could not send that photo. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  /** Insert an emoji at the caret (or over the selection), like the home composer. */
  function insertEmoji(emoji: string) {
    const el = textRef.current;
    if (!el) {
      setText((v) => v + emoji);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emoji + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <Wrap>
      {file && previewUrl && (
        <PreviewStrip data-testid="message-attachment-preview">
          <PreviewThumb src={previewUrl} alt={file.name} />
          <RemoveBtn
            type="button"
            aria-label={`Remove ${file.name}`}
            onClick={() => {
              setFile(null);
              setErr(null);
              clearFileInput();
            }}
          >
            Remove
          </RemoveBtn>
        </PreviewStrip>
      )}
      {err && <ErrorLine role="alert">{err}</ErrorLine>}
      <Bar
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {/* Attach (+) — a real, hidden file input. One image per message,
            matching the server's one-image-per-DM rule. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="message-file-input"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const picked = e.target.files?.[0] ?? null;
            if (picked) {
              setFile(picked);
              setErr(null);
            }
            // Reset so re-picking the same file still fires a change event.
            clearFileInput();
          }}
        />
        {disabled ? (
          <DisabledHint explanation={disabledReason}>
            {(describedBy) => (
              <IconButton
                icon="plus"
                label="Add photo"
                aria-disabled="true"
                aria-describedby={describedBy}
                data-testid="composer-attach"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            )}
          </DisabledHint>
        ) : (
          <IconButton
            icon="plus"
            label="Add photo"
            data-testid="composer-attach"
            onClick={() => fileInputRef.current?.click()}
          />
        )}
        {disabled ? (
          <DisabledHint explanation={disabledReason}>
            {(describedBy) => (
              <IconButton
                icon="smile"
                label="Add emoji"
                aria-disabled="true"
                aria-describedby={describedBy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            )}
          </DisabledHint>
        ) : (
          <EmojiPicker onSelect={insertEmoji} />
        )}
        <Input
          ref={textRef}
          aria-label="Message"
          placeholder={file ? 'Add a caption…' : 'Type a message…'}
          rows={1}
          value={text}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setText(e.target.value);
            onTyping?.();
          }}
          onKeyDown={onKeyDown}
        />
        {/* Voice messages are not built — say so rather than shipping a button
            that does nothing when pressed. */}
        <DisabledHint
          explanation={disabledReason ?? "Voice messages aren't available yet."}
        >
          {(describedBy) => (
            <IconButton
              icon="mic"
              label="Record voice message (unavailable)"
              aria-disabled="true"
              aria-describedby={describedBy}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          )}
        </DisabledHint>
        <Button type="submit" variant="primary" disabled={!canSend}>
          {busy ? 'Sending…' : 'Send'}
        </Button>
      </Bar>
    </Wrap>
  );
}
